import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  signal,
  Signal,
  viewChild,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { LoadPartnerDiscountsListAction } from '@app/modules/pricing/store/discount.actions';
import { AddPricingPartnerBundleAction } from '@app/modules/pricing/store/pricing-partner.actions';
import { PricingPartnerState } from '@app/modules/pricing/store/pricing-partner.state';
import { PricingPartner } from '@app/modules/pricing/types/pricing.types';
import { Bundle } from '@core/core.types';
import { ProductsListState } from '@core/store/products/products.state';
import { momentToNgbDate } from '@core/utils/core.util';
import {
  NgbDatepickerModule,
  NgbTooltipModule,
  NgbTypeahead,
  NgbTypeaheadModule,
} from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { LoadingService } from '@shared/components/loading-dialog/loading-dialog.component';
import { ValidationErrorComponent } from '@shared/components/validation-error/validation-error.component';
import moment from 'moment';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  Subject,
  tap,
} from 'rxjs';

@Component({
  selector: 'app-bundle-price-form',
  imports: [
    NgbTypeaheadModule,
    NgbDatepickerModule,
    ReactiveFormsModule,
    FormsModule,
    TranslatePipe,
    NgbTooltipModule,
    ValidationErrorComponent,
  ],
  templateUrl: './bundle-price-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './bundle-price-form.component.scss',
})
export class BundlePriceFormComponent implements OnInit {
  @ViewChild('instance', { static: true }) instance: NgbTypeahead;
  public partnerId = input<string>();
  private fb = inject(FormBuilder);
  private store = inject(Store);
  private loadingService = inject(LoadingService);
  private translateService = inject(TranslateService);

  protected focus$ = new Subject<string>();
  protected click$ = new Subject<string>();

  protected bundleForm = this.fb.group({
    bundle: new FormControl<Bundle>(null, Validators.required),
    price: new FormControl<number>({ value: null, disabled: true }, [
      Validators.required,
      Validators.min(1),
    ]),
    validFrom: new FormControl<moment.Moment>(
      { value: null, disabled: true },
      Validators.required
    ),
  });
  protected bundleTypeaheadDisabled = signal<boolean>(false);
  protected noCurrencyWarning = signal<boolean>(false);

  protected bundlesList: Signal<Bundle[]>;
  protected pricingPartner: Signal<PricingPartner>;
  protected firstTimePrice = signal<boolean>(false);

  protected minDate = moment().add(1, 'day');
  protected maxDate = moment().add(100, 'year');
  protected startDate = moment().add(1, 'month');

  private lastSearchTerm = '';

  private bundleTypeaheadElement = viewChild<ElementRef<HTMLInputElement>>(
    'bundleTypeaheadElement'
  );

  constructor() {
    effect(() => {
      if (this.pricingPartner()) {
        if (!this.pricingPartner().currency) {
          this.bundleTypeaheadDisabled.set(true);
          this.noCurrencyWarning.set(true);
        } else {
          this.bundleForm.get('price').enable();
          this.bundleTypeaheadDisabled.set(false);
          this.noCurrencyWarning.set(false);
        }
      }
    });
  }

  ngOnInit(): void {
    this.pricingPartner = computed(() =>
      this.store.selectSignal(PricingPartnerState.getPricingPartnerById)()(
        this.partnerId()
      )
    );

    this.store.dispatch(new LoadPartnerDiscountsListAction(this.partnerId()));

    this.bundlesList = computed(() =>
      this.store.selectSignal(ProductsListState.getBundlesList)()
    );

    this.bundleForm.get('bundle').valueChanges.subscribe((value) => {
      if (!value) {
        return;
      }

      const price = this.pricingPartner()?.pricelist.find(
        (b) => b.id === value.id
      )?.pivot?.price;

      if (price) {
        this.bundleForm.get('validFrom').enable();
        this.bundleForm.get('validFrom').setValue(null);
        this.firstTimePrice.set(false);
      } else if (!price) {
        this.firstTimePrice.set(true);
        this.bundleForm.get('validFrom').disable();
        this.bundleForm
          .get('validFrom')
          .setValue(moment().year(1970).month(0).date(1));
      }
    });
  }

  protected search = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(
      tap((term) => {
        if (this.lastSearchTerm !== term) {
          this.bundleForm.get('bundle').setValue(null);
        }

        this.lastSearchTerm = term;
      }),
      debounceTime(200),
      distinctUntilChanged()
    );
    const clicksWithClosedPopup$ = this.click$.pipe(
      filter(() => (this.instance ? !this.instance.isPopupOpen() : false))
    );
    const inputFocus$ = this.focus$;

    return merge(debouncedText$, inputFocus$, clicksWithClosedPopup$).pipe(
      map((term) =>
        this.bundlesList()?.filter(
          (v) => v.internalName.toLowerCase().indexOf(term.toLowerCase()) > -1
        )
      )
    );
  };

  protected addBundle() {
    const formValue = this.bundleForm.getRawValue();

    this.loadingService.wait({
      message: this.translateService.instant(
        'pricing.partner.details.loading.addingPrice'
      ),
      observableToFinish$: this.store.dispatch(
        new AddPricingPartnerBundleAction(
          this.partnerId(),
          formValue.bundle?.id,
          formValue.price,
          this.firstTimePrice() ? null : formValue.validFrom?.toDate() || null
        )
      ),
    });

    this.bundleForm.reset();
    this.bundleForm.get('validFrom').disable();
    this.bundleTypeaheadElement().nativeElement.value = '';
    this.bundleForm.updateValueAndValidity();
  }

  protected inputFormatter = (item: Bundle) => item?.name ?? '';

  protected readonly momentToNgbDate = momentToNgbDate;
}
